import React, { type FC, useState, useEffect } from 'react'
import { Events, Socket, WebRTC, Island, RestAPI } from './components'
import { Provider } from 'react-redux'
import { store } from './store'
import { Base64 } from 'js-base64'
import wakeUpWorker from './workers/wake_up'
import { initI18n } from './lib/i18n'

import 'react-tooltip/dist/react-tooltip.css'
import { useEventListener, eventDispatch, setJSONItem, getJSONItem } from './utils'
import { detach } from './lib/webrtc/messages'
import { checkDarkTheme, setTheme } from './lib/darkTheme'
import { changeOperatorStatus } from './services/user'
import { isEmpty } from './utils/genericFunctions/isEmpty'
import { checkInternetConnection } from './utils/genericFunctions/checkConnection'
import { isBackCallActive } from './utils/genericFunctions/isBackCallVisible'

interface PhoneIslandProps {
  dataConfig: string
  showAlways?: boolean
  uaType: string
}

interface DeviceInputOutputTypes {
  deviceId: string
}

export const PhoneIsland: FC<PhoneIslandProps> = ({
  dataConfig,
  showAlways = false,
  uaType,
}: PhoneIslandProps) => {
  const CONFIG: string[] = Base64.atob(dataConfig || '').split(':')
  const HOST_NAME: string = CONFIG[0]
  const USERNAME: string = CONFIG[1]
  const AUTH_TOKEN: string = CONFIG[2]
  const SIP_EXTEN: string = CONFIG[3]
  const SIP_SECRET: string = CONFIG[4]
  const SIP_HOST: string = CONFIG[5]
  const SIP_PORT: string = CONFIG[6]

  // Initialize the state to manage the reload events
  const [reload, setReload] = useState<boolean>(false)
  const [reloadedWebRTC, setReloadedWebRTC] = useState<boolean>(false)
  const [reloadedSocket, setReloadedSocket] = useState<boolean>(false)

  useEffect(() => {
    const worker = new Worker(wakeUpWorker, { type: 'module' })
    worker.onmessage = (event: MessageEvent<string>) => {
      // Handle wakeup message
      if (event.data === 'wakeup') {
        setReload(true)
      }
    }

    return () => {
      worker.terminate()
    }
  }, [])

  useEffect(() => {
    if (reloadedSocket && reloadedWebRTC) {
      setReload(false)
      setReloadedWebRTC(false)
      setReloadedSocket(false)
    }
  }, [reloadedSocket, reloadedWebRTC])

  useEventListener('phone-island-expand', () => {
    store.dispatch.island.toggleIsOpen(true)
    eventDispatch('phone-island-expanded', {})
  })
  useEventListener('phone-island-compress', () => {
    store.dispatch.island.toggleIsOpen(false)
    eventDispatch('phone-island-compressed', {})
  })

  useEventListener('phone-island-call-keypad-close', () => {
    store.dispatch.island.setIslandView('call')
    eventDispatch('phone-island-call-keypad-closed', {})
  })
  useEventListener('phone-island-call-transfer-close', () => {
    store.dispatch.island.setIslandView('call')
    eventDispatch('phone-island-call-transfer-closed', {})
  })
  useEventListener('phone-island-recording-close', () => {
    store.dispatch.island.setIslandView(null)
    eventDispatch('phone-island-recording-closed', {})
  })
  useEventListener('phone-island-audio-player-close', () => {
    store.dispatch.island.setIslandView(null)
    eventDispatch('phone-island-audio-player-closed', {})
  })

  useEventListener('phone-island-detach', (data) => {
    detach()
    eventDispatch('phone-island-detached', {})
  })

  useEventListener('phone-island-audio-input-change', (data: DeviceInputOutputTypes) => {
    setJSONItem('phone-island-audio-input-device', { deviceId: data.deviceId })
    eventDispatch('phone-island-audio-input-changed', {})
  })

  useEventListener('phone-island-video-input-change', (data: DeviceInputOutputTypes) => {
    setJSONItem('phone-island-video-input-device', { deviceId: data.deviceId })
    eventDispatch('phone-island-video-input-changed', {})
  })

  const [firstRenderI18n, setFirstRenderI18n] = useState(true)
  const [firstAudioOutputInit, setFirstAudioOutputInit] = useState(true)

  useEventListener('phone-island-audio-output-change', (data: DeviceInputOutputTypes) => {
    if (!firstAudioOutputInit) {
      eventDispatch('phone-island-call-start', { number: '*43' })
      store.dispatch.island.setIslandView(null)
      store.dispatch.island.toggleAvoidToShow(true)
    }

    setTimeout(() => {
      const remoteAudioElement: any = store.getState().player.remoteAudio
      // set audio output
      remoteAudioElement?.current
        .setSinkId(data.deviceId)
        .then(function () {
          console.info('Default audio output device change with success!')
          // set device to localstorage
          setJSONItem('phone-island-audio-output-device', { deviceId: data.deviceId })

          // dispatch event
          eventDispatch('phone-island-audio-output-changed', {})
          eventDispatch('phone-island-call-end', {})
          store.dispatch.island.toggleAvoidToShow(false)
        })
        .catch(function (err) {
          console.error('Default audio output device change error:', err)
          eventDispatch('phone-island-call-end', {})
          store.dispatch.island.toggleAvoidToShow(false)
        })
    }, 500)
  })

  // Listen for the operator status change
  useEventListener('phone-island-presence-change', (data: any) => {
    changeOperatorStatus(data)
    eventDispatch('phone-island-presence-changed', {})
  })

  useEventListener('phone-island-view-changed', (data) => {
    const viewType = data?.viewType
    store.dispatch.island.setIslandView(viewType)
  })

  //initialize i18n
  useEffect(() => {
    if (firstRenderI18n) {
      initI18n()
      setFirstRenderI18n(false)
    }
  }, [firstRenderI18n])

  const remoteAudioElement: any = store.getState().player.remoteAudio

  //get output device from localstorage
  useEffect(() => {
    if (firstAudioOutputInit && remoteAudioElement) {
      const defaultAudioOutputDevice: any = getJSONItem(
        `phone-island-audio-output-device`,
      )?.deviceId
      eventDispatch('phone-island-audio-output-change', {
        deviceId: defaultAudioOutputDevice,
      })
      setFirstAudioOutputInit(false)
    }
  }, [firstAudioOutputInit, remoteAudioElement])

  useEffect(() => {
    checkDarkTheme()
  }, [])

  useEventListener('phone-island-theme-change', (theme: any) => {
    setTheme(theme?.selectedTheme)
  })

  useEventListener('phone-island-default-device-change', (data) => {
    store.dispatch.currentUser.updateCurrentDefaultDevice(data?.deviceInformationObject)
    eventDispatch('phone-island-default-device-changed', {})
  })

  useEventListener('phone-island-alert', (alertType: any) => {
    store.dispatch.alerts.setAlert(alertType.toString())
  })

  // Manually check if internet connection is enabled or not
  useEventListener('phone-island-check-connection', () => {
    checkInternetConnection().then((internetIsActive) => {
      if (internetIsActive) {
        eventDispatch('phone-island-internet-connected', {})
      } else {
        eventDispatch('phone-island-internet-disconnected', {})
      }
    })
  })

  // Check internet connection every 5 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkInternetConnection().then((internetIsActive) => {
        if (internetIsActive) {
          eventDispatch('phone-island-internet-connected', {})
        } else {
          eventDispatch('phone-island-internet-disconnected', {})
        }
      })
    }, 5000)

    return () => clearInterval(intervalId)
  }, [])

  useEventListener('phone-island-main-presence', (data: any) => {
    const currentUsernameInformation: any = store.getState().currentUser?.username
    const currentUserObject: any = store.getState().currentUser
    let mainPresenceValueBeforeUpdate = currentUserObject?.mainPresence
    if (
      currentUsernameInformation !== undefined &&
      currentUsernameInformation !== '' &&
      !isEmpty(data[currentUsernameInformation]) &&
      data[currentUsernameInformation]?.mainPresence !== undefined
    ) {
      let newMainPresenceValue = data[currentUsernameInformation]?.mainPresence
      store.dispatch.currentUser.updateMainPresence(data[currentUsernameInformation]?.mainPresence)
      let mainPresenceValueAfterUpdate = newMainPresenceValue
      if (mainPresenceValueAfterUpdate === 'online' && mainPresenceValueBeforeUpdate !== 'online') {
        eventDispatch('phone-island-call-ended', {})
      }
    }
  })

  useEventListener('phone-island-call-status', () => {
    const callInformation = store.getState().currentCall
    console.log('Call status debug informations: ', callInformation)
  })

  useEventListener('phone-island-user-status', () => {
    const userInformation = store.getState().currentUser
    console.log('User status debug informations: ', userInformation)
  })

  useEventListener('phone-island-all-users-status', () => {
    const allUsersInformation = store.getState().users
    console.log('Users status debug informations: ', allUsersInformation)
  })

  useEventListener('phone-island-status', () => {
    const phoneIslandInformation = store.getState().island
    console.log('Phone island status debug informations: ', phoneIslandInformation)
  })

  useEventListener('phone-island-webrtc-status', () => {
    const webrtcInformation = store.getState().webrtc
    console.log('Webrtc status debug informations: ', webrtcInformation)
  })

  useEventListener('phone-island-player-status', () => {
    const playerInformation = store.getState().player
    console.log('Player status debug informations: ', playerInformation)
  })

  useEventListener('phone-island-conference-status', () => {
    const conferenceInformation = store.getState().conference
    console.log('Webrtc status debug informations: ', conferenceInformation)
  })

  useEventListener('phone-island-streaming-status', () => {
    const streamingInformation = store.getState().streaming
    console.log('Streaming status debug informations: ', streamingInformation)
  })

  useEventListener('phone-island-player-force-stop', () => {
    store.dispatch.player.reset()
    console.log('Audio player is interrupted')
  })

  useEventListener('phone-island-sideview-open', () => {
    store.dispatch.island.toggleSideViewVisible(true)
    eventDispatch('phone-island-sideview-opened', {})
  })

  useEventListener('phone-island-sideview-close', () => {
    store.dispatch.island.toggleSideViewVisible(false)
    eventDispatch('phone-island-sideview-closed', {})
  })

  useEventListener('phone-island-size-change', (args: any) => {
    const { sideViewIsVisible } = store.getState().island

    // Get current dimensions from args
    const { sizeInformation } = args

    // // Calculate extra row dimension ( side view and back call )
    const updatedSizeInformation = {
      ...sizeInformation,
      right: sideViewIsVisible ? '42px' : '0px',
      top: isBackCallActive() ? '40px' : '0px',
    }
    eventDispatch('phone-island-size-changed', { sizes: updatedSizeInformation })
  })

  // Listen for the call end event and set the island size to 0
  useEventListener('phone-island-call-ended', () => {
    const { mainPresence } = store.getState().currentUser
    if (mainPresence === 'online') {
      const sizeInformation: any = {
        width: '0px',
        height: '0px',
      }
      eventDispatch('phone-island-size-change', { sizeInformation })
      eventDispatch('phone-island-sideview-close', {})
      store.dispatch.island.resetIslandStore()
    }
  })

  useEventListener('phone-island-conference-list-open', () => {
    store.dispatch.island.toggleConferenceList(true)
    eventDispatch('phone-island-conference-list-opened', {})
  })

  useEventListener('phone-island-alert-removed', (alertRemovedType) => {
    // Get current alerts status
    const { activeAlertsCount } = store.getState().alerts.status
    const { view, previousView } = store.getState().island
    const { isActive } = store.getState().conference
    const alertsData = store.getState().alerts.data
    const currentCall = store.getState().currentCall
    const { incoming, outgoing, accepted } = currentCall

    // Check if alert type was provided
    const alertType = alertRemovedType?.type

    // Check if user is in a call
    const isInCall =
      currentCall.incoming ||
      currentCall.outgoing ||
      currentCall.accepted ||
      currentCall.conversationId !== ''

    // Determine if the island should remain visible
    const shouldKeepVisible =
      incoming ||
      outgoing ||
      accepted ||
      activeAlertsCount > 0 ||
      view === 'player' ||
      view === 'recorder' ||
      view === 'physicalPhoneRecorder' ||
      (view === 'waitingConference' && isActive) ||
      (view === 'transfer' && isActive) ||
      (view === 'settings' && isActive) ||
      (view === 'settings' && (previousView === 'recorder' || previousView === 'player'))

    // Reset the island store only if:
    // 1. The island should not remain visible
    // 2. No more active alerts
    // 3. The specific alert is not active anymore
    // 4. User is not currently in a call
    if (
      !shouldKeepVisible &&
      activeAlertsCount === 0 &&
      (!alertType || (alertsData[alertType] && !alertsData[alertType].active)) &&
      !isInCall
    ) {
      const sizeInformation: any = {
        width: '0px',
        height: '0px',
      }
      eventDispatch('phone-island-size-change', { sizeInformation })
      eventDispatch('phone-island-sideview-close', {})
      store.dispatch.island.resetIslandStore()
    }
  })

  useEventListener('phone-island-conference-list-close', () => {
    store.dispatch.island.toggleConferenceList(false)
    eventDispatch('phone-island-conference-list-closed', {})
  })

  return (
    <>
      <Provider store={store}>
        <WebRTC
          hostName={HOST_NAME}
          sipExten={SIP_EXTEN}
          sipSecret={SIP_SECRET}
          sipHost={SIP_HOST}
          sipPort={SIP_PORT}
          reload={reload}
          reloadedCallback={() => setReloadedWebRTC(true)}
          uaType={uaType}
        >
          <RestAPI hostName={HOST_NAME} username={USERNAME} authToken={AUTH_TOKEN}>
            <Socket
              hostName={HOST_NAME}
              username={USERNAME}
              authToken={AUTH_TOKEN}
              reload={reload}
              reloadedCallback={() => setReloadedSocket(true)}
              uaType={uaType}
            >
              <Events sipHost={SIP_HOST}>
                <Island showAlways={showAlways} uaType={uaType} />
              </Events>
            </Socket>
          </RestAPI>
        </WebRTC>
      </Provider>
    </>
  )
}

PhoneIsland.displayName = 'PhoneIsland'
